import * as React from "react";
import { SlideWireframe } from "./SlideWireframe";
import { PlusIcon, ArrowRight, ChevronRight } from "lucide-react";
import { Button, Spin } from "antd";
import { DialogMessageType } from "@/types/commands";
import { StepIndicator } from "@/components/ui/StepIndicator";
import { useContentFramingStore } from "@/state/contentFramingStore";
import Ring from "@/components/ui/Ring";
import TitleAndSource from "./TitleAndSource";
import KeyTakeAways from "./KeyTakeAways";
import { sendSlideShapesToServer } from "@/helpers/slide-wireframing/getAllShapesFromPlaceholderSlide";
import { logger } from "@/helpers/tslog";
import Script from "./Script";

export function ContentFramingContent() {
  const [isLoadingShapes, setIsLoadingShapes] = React.useState(false);
  const [showKeyTakeAways, setShowKeyTakeAways] = React.useState(true);
  const [showScript, setShowScript] = React.useState(false);
  
  const {
    questionsPerStep,
    stepLabels,
    stepSizes,
    setCurrentStep,
    rows,
    placeholderOptions,
    setHoveredShapeId,
    toggleRow,
    setSelection,
    setInputText,
    setMode,
    updateFieldsFromShape,
    loadInputTextFromMetadata,
    setSlideShapesData,
    setSlideDimensions,
    updateRowsFromLabeledShapes,
    updateAnswered,
  } = useContentFramingStore();
  const currentStep = useContentFramingStore((state) => state.currentStep);
  const answersByStep = useContentFramingStore((state) => state.answersByStep);

  // Load input text from metadata when component mounts
  React.useEffect(() => {
    loadInputTextFromMetadata();
  }, [loadInputTextFromMetadata]);

  // Fetch data from localStorage and send to LLM server
  React.useEffect(() => {
    const fetchAndSendToLLM = async () => {
      setIsLoadingShapes(true);
      try {
        // Get data from localStorage
        const raw = localStorage.getItem("slideShapesData");
        if (!raw) {
          setIsLoadingShapes(false);
          return;
        }
        
        const labeledSlideShapesData = JSON.parse(raw);
        
        // Check if we have the necessary data
        if (!labeledSlideShapesData || !labeledSlideShapesData.shapes) {
          logger.warn("No slide shapes data found in localStorage");
          setIsLoadingShapes(false);
          return;
        }
        
        // Send to LLM server
        const { labeledShapes , slideDimensions } = await sendSlideShapesToServer(labeledSlideShapesData);
        
        // Use LLM labeled shapes if available, otherwise fallback to labeled shapes
        const finalShapes = (labeledShapes && labeledShapes.length > 0) ? labeledShapes : labeledSlideShapesData.shapes;
        
        // Store to slideShapesData in the content framing store
        if (finalShapes && finalShapes.length > 0) {
          setSlideShapesData(finalShapes);
          setSlideDimensions(slideDimensions);
          
          // Update rows with selections and disabledText based on labeled shapes
          updateRowsFromLabeledShapes(finalShapes);
          
          // Update localStorage with the LLM processed data
          const labeledSlideShapesDataWithLLM = {
            ...labeledSlideShapesData,
            shapes: finalShapes
          };
          localStorage.setItem("slideShapesData", JSON.stringify(labeledSlideShapesDataWithLLM));
          
        }
      } catch (error) {
        logger.error("Error fetching and sending data to LLM:", error);
      } finally {
        setIsLoadingShapes(false);
        localStorage.removeItem("metadata");
      }
    };
    
    fetchAndSendToLLM();
  }, [setSlideShapesData, updateRowsFromLabeledShapes]);


  return (
    <div className="bg-gray-100 w-full relative">
      {/* Loading Overlay */}
      {isLoadingShapes && (
        <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex items-center justify-center z-50">
          <div className="flex flex-col items-center gap-4">
            <Spin size="large" />
            <p className="text-lg font-semibold text-gray-700">Content Framing Loading...</p>
          </div>
        </div>
      )}
      
      <div className="p-2 px-6 flex justify-between items-center bg-as-dark-blue">
        <h2 className="text-white text-2xl font-bold">Content Framing</h2>
        <img src="assets/AlphaScript-logo.png" alt="" className="w-30 h-auto object-contain" />
      </div>

      <div className="my-6 pl-6 w-[calc(60%_-_2rem)]">
          <Ring className="p-4">
            <StepIndicator
            currentStep={currentStep}
            answersByStep={answersByStep}
            questionsPerStep={questionsPerStep}
            labels={stepLabels}
            stepSizes={stepSizes}
            onStepClick={setCurrentStep}
            labelClassName="text-[1.25rem]"
            />
          </Ring>
        </div>

      <div className=" pb-6 px-6  flex flex-row items-start justify-center gap-8 w-full">
       
      
        {/* <Divider style={{ borderColor: "black" }}/> */}
        <div className="w-[60%]">

          {currentStep === 0 && (
          <TitleAndSource
            rows={rows}
            placeholderOptions={placeholderOptions}
            onToggle={toggleRow}
            onSelectionChange={setSelection}
            onInputTextChange={setInputText}
            onModeChange={setMode}
            onUpdateFieldsFromShape={updateFieldsFromShape}
            onHoverShape={setHoveredShapeId}
          />
          )}
          {currentStep === 1 && (
            <>
              {!showKeyTakeAways && (
              <Ring className="mt-4 py-2 px-4">
                <div className="flex gap-2 items-center text-lg font-bold cursor-pointer"  onClick={() => {
                  setShowKeyTakeAways(!showKeyTakeAways);
                  setShowScript(false);
                }}> Key Take-Aways <ChevronRight className="w-4 h-4 font-bold" color="black" /></div>
              </Ring>
              )}
              {showKeyTakeAways && (
                <>
                 <div className="flex items-start justify-between mb-4">
                 <h2 className="text-2xl font-bold text-gray-900">Key Take-Aways</h2>
               </div>
               <Ring className="py-8 px-4">
                <KeyTakeAways
                  rows={rows}
                  placeholderOptions={placeholderOptions}
                  onToggle={toggleRow}
                  onSelectionChange={setSelection}
                  onInputTextChange={setInputText}
                  onModeChange={setMode}
                  onHoverShape={setHoveredShapeId}
                  onUpdateFieldsFromShape={updateFieldsFromShape}
                />
                 {currentStep === 1 && (
                  <div className="mt-4 flex items-center justify-end gap-3 px-4">
                    <Button style={{
                      backgroundColor: 'var(--color-as-dark-gray)',
                      borderColor: 'var(--color-as-dark-gray)',
                      color: 'white',
                      fontWeight: 500,
                      paddingLeft: '14px',
                      paddingRight: '14px',
                      height: '34px',
                      fontSize: '13px',
                      borderRadius: '9999px'
                    }} 
                    onClick={() => {
                      updateAnswered(1, 0);
                      setShowKeyTakeAways(true);
                      setShowScript(false);
                      setCurrentStep(0);
                    }}>Back</Button>
                    <Button type="primary" style={{
                        paddingLeft: '14px',
                        paddingRight: '14px',
                        height: '34px',
                        fontSize: '13px',
                        borderRadius: '9999px'
                      }} onClick={() => {
                      updateAnswered(1, 1);
                      setShowKeyTakeAways(!showKeyTakeAways);
                      setShowScript(true);
                    }}>Next
                     <ArrowRight className="w-4 h-4" color="white" />
                    </Button>
                  </div>
                )}
                </Ring>
                </>
              )}
              {!showScript && (
            <Ring className="mt-4 py-2 px-4">
              <div className="flex gap-2 items-center text-lg font-bold cursor-pointer" onClick={() => {
                setShowScript(!showScript);
                setShowKeyTakeAways(false);
              }}> Script <ChevronRight className="w-4 h-4 font-bold" color="black" /></div>
            </Ring>
              )}
              {showScript && (
            <Script
              rows={rows}
              placeholderOptions={placeholderOptions}
              onToggle={toggleRow}
              onSelectionChange={setSelection}
              onInputTextChange={setInputText}
              onHoverShape={setHoveredShapeId}
              onUpdateFieldsFromShape={updateFieldsFromShape}
            />
              )}
            </>
          )}
          {/* Actions: Cancel (gray) and Updates Confirmed (green) */}
          {(currentStep === 0 || answersByStep[1] === 1) && (
          <div className="mt-6 flex items-center justify-end gap-3 px-4">
          <Button
            className="rounded-3xl hover:brightness-110"
            style={{
              backgroundColor: 'var(--color-as-dark-gray)',
              borderColor: 'var(--color-as-dark-gray)',
              color: 'white',
              fontWeight: 500,
              paddingLeft: '14px',
              paddingRight: '14px',
              height: '34px',
              fontSize: '13px',
              borderRadius: '9999px'
            }}
            onClick={() => {
              // Go back or cancel current step
              if(currentStep === 1){
                setShowKeyTakeAways(true);
                setShowScript(false);
                updateAnswered(1, 0);
                return;
              } else if(currentStep === 0){
                Office.context.ui.messageParent(DialogMessageType.CLOSE);
                setCurrentStep(0);
                return;
              }
            }}
          >
            {currentStep === 0 ? "Cancel" : "Back"}
          </Button>

          <Button
            className="rounded-3xl hover:brightness-110"
            style={{
              backgroundColor: 'var(--color-as-green-primary, #10B981)', // fallback emerald-500
              borderColor: 'var(--color-as-green-primary, #10B981)',
              color: 'white',
              fontWeight: 600,
              paddingLeft: '18px',
              paddingRight: '16px',
              height: '40px',
              fontSize: '14px',
              borderRadius: '9999px'
            }}
            onClick={() => {
              // Advance to next step
              if(currentStep === 1){
                const rows = useContentFramingStore.getState().rows;
                localStorage.setItem("contentFramingRowsData", JSON.stringify(rows));
                Office.context.ui.messageParent(DialogMessageType.INSERT_DATA_TO_PLACEHOLDER);
                return;
              } 
              if( currentStep === 0){
                setCurrentStep( currentStep + 1);
              }
            }}
            onMouseEnter={(e) => {
              const c = getComputedStyle(document.documentElement)
                .getPropertyValue('--color-as-green-primary') || '#10B981';
              e.currentTarget.style.backgroundColor = c.trim();
              e.currentTarget.style.borderColor = c.trim();
            }}
            onMouseLeave={(e) => {
              const c = getComputedStyle(document.documentElement)
                .getPropertyValue('--color-as-green-primary') || '#10B981';
              e.currentTarget.style.backgroundColor = c.trim();
              e.currentTarget.style.borderColor = c.trim();
            }}
          >
            {currentStep === 1 ? (
              <div className="flex items-center justify-center gap-2">
                <span>Complete</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <span>Updates Confirmed</span>
                <ArrowRight className="w-4 h-4" color="white" />
              </div>
            )}

          </Button>
          </div>
          )}
        </div>

        <div className="w-[40%]"> 
              <div className="flex items-start justify-between">
           <h2 className="text-2xl font-bold text-gray-900">Slide Wire-Frame</h2>
          </div>
          <SlideWireframe />
          <div className="mt-3">
            <div className="flex items-start justify-end">
              <Button 
                size="small"
                className="rounded-3xl hover:brightness-110"
                style={{
                  backgroundColor: 'var(--color-as-dark-gray)',
                  borderColor: 'var(--color-as-dark-gray)',
                  color: 'white',
                  fontWeight: 500,
                  paddingLeft: '6px',
                  paddingRight: '6px',
                  height: '28px',
                  fontSize: '12px',
                  borderRadius: '9999px'
                }}
                onClick={() => {
                  Office.context.ui.messageParent(DialogMessageType.OPEN_CONFIRM_PLACEHOLDER);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-as-dark-gray)';
                  e.currentTarget.style.borderColor = 'var(--color-as-dark-gray)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-as-dark-gray)';
                  e.currentTarget.style.borderColor = 'var(--color-as-dark-gray)';
                }}
                >
                 <PlusIcon className="w-3 h-3 -mr-1" color="white" />
                Add Placeholder
              </Button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}


